/**
 * Fein-ACL-Editor pro (Modul, Subject) — Joerg 2026-08-20.
 *
 * Zeigt die Fein-Rechte-Matrix eines Moduls fuer eine Person, Gruppe oder
 * default-Zuweisung. Jede Zeile ist ein Permission-Slug aus dem Katalog
 * (portal_module_registry.permissions_catalog) — Haekchen setzbar auf:
 *   ✓ (allowed=true)   — explizit erlaubt
 *   ✗ (allowed=false)  — explizit verboten
 *   ─ (allowed=null)   — kein Eintrag, Level-Preset gilt (Fallback)
 *
 * „Preset anwenden" (seit 2026-10-01, Dokument-Navigation Abhakliste 2.02, Jörg: „Preset mit
 * Rückfrage bauen"): Der Knopf belegt die Haken nur VOR — nach einer Rückfrage, die sagt, wie
 * viele Haken sich für wen ändern. Geschrieben wird ausschließlich über „Speichern (n)“; bis
 * dahin lassen sich die Haken einzeln korrigieren oder verwerfen. Auslöser: Am 30.09. schrieb ein
 * Preset-Klick bei Meiners 16 Rechte der Gruppe Handwerker sofort und ohne Protokoll
 * (Fehlerliste `Meiners Pilot/Fehlerlisten/2026-09-30-elias-sieht-zeiten-von-juergen.md`).
 * Das Backend protokolliert jeden Schreibvorgang in addressbook.audit_log.
 *
 * REST-Endpoints (Portal-Backend admin-acl-permissions.ts):
 *   GET  /api/admin/acl/module-permissions?module=&subject_type=&subject_id=
 *   PUT  /api/admin/acl/module-permissions          (changes + preset-Notiz fürs Protokoll)
 *   POST /api/admin/acl/module-permissions/apply-preset — Altweg, hier nicht mehr genutzt
 *
 * Wird eingebunden von:
 *   - AdressbuchPersonAkte.tsx (Zugriffe-Tab, subject_type='person')
 *   - AdminAdressbuchModuleDetailPage.tsx (Modul-Sicht, subject_type='person'|'group'|'default')
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, registerDirtySource } from '@onvice/portal-shell-api';
import { useDialogs } from '@onvice/portal-ui';

type SubjectType = 'person' | 'group' | 'default';
type PresetLevel = 'reader' | 'tester' | 'author' | 'editor' | 'admin';
type Allowed = boolean | null;

interface CatalogEntry {
  slug: string;
  label: string;
  category?: string;
  presetLevel?: PresetLevel;
}
interface ValueEntry {
  permission_slug: string;
  allowed: boolean;
}
interface GetResponse {
  module: string;
  subject_type: SubjectType;
  subject_id: string | null;
  catalog: CatalogEntry[];
  values: ValueEntry[];
}

interface Props {
  moduleSlug: string;
  subjectType: SubjectType;
  subjectId: string | null;
  /** Name für Rückfragen, z. B. „Gruppe Handwerker" oder „Max Mustermann"; Standard nach subjectType */
  subjectLabel?: string;
  /** Aktueller ACL-Level des Subjects auf dem Modul — fuer den Preset-Button.
   *  Wenn undefined, wird kein Preset-Button angezeigt (Sub-Sicht ohne Level-Info). */
  currentLevel?: PresetLevel;
  /** Callback nach erfolgreichem Save — Parent kann Bestand neu laden. */
  onChanged?: () => void;
}

const PRESET_LEVELS: PresetLevel[] = ['reader', 'tester', 'author', 'editor', 'admin'];
const LEVEL_LABEL: Record<PresetLevel, string> = {
  reader: 'Leser',
  tester: 'Tester',
  author: 'Autor',
  editor: 'Editor',
  admin:  'Admin',
};
// Gleiche Rangfolge wie im Backend (admin-acl-permissions.ts LEVEL_RANK).
const LEVEL_RANK: Record<PresetLevel, number> = { reader: 2, tester: 3, author: 4, editor: 5, admin: 6 };

/** Haken, die ein Preset setzt: erlaubt, wenn das Preset mindestens das Niveau des Rechts hat. */
export function expandPreset(catalog: CatalogEntry[], preset: PresetLevel): Array<[string, boolean]> {
  return catalog
    .filter((e): e is CatalogEntry & { presetLevel: PresetLevel } => e.presetLevel != null)
    .map((e) => [e.slug, LEVEL_RANK[preset] >= LEVEL_RANK[e.presetLevel]]);
}

function defaultSubjectLabel(subjectType: SubjectType): string {
  if (subjectType === 'group') return 'diese Gruppe';
  if (subjectType === 'person') return 'diese Person';
  return 'den Standard (alle ohne eigenen Eintrag)';
}

export default function ModulePermissionsMatrix({
  moduleSlug,
  subjectType,
  subjectId,
  subjectLabel,
  currentLevel,
  onChanged,
}: Props) {
  const dlg = useDialogs();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [values, setValues] = useState<Map<string, Allowed>>(new Map());
  const [dirty, setDirty] = useState<Map<string, Allowed>>(new Map());
  // Aus welchem Preset die Vorbelegung stammt — nur eine Notiz fürs Protokoll.
  const [presetNote, setPresetNote] = useState<PresetLevel | null>(null);
  const whom = subjectLabel?.trim() || defaultSubjectLabel(subjectType);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = new URLSearchParams({
        module: moduleSlug,
        subject_type: subjectType,
      });
      if (subjectId) params.set('subject_id', subjectId);
      const res = await api.get<{ success: boolean; data: GetResponse }>(
        `/admin/acl/module-permissions?${params.toString()}`,
      );
      setCatalog(res.data.catalog);
      const m = new Map<string, Allowed>();
      for (const v of res.data.values) m.set(v.permission_slug, v.allowed);
      setValues(m);
      setDirty(new Map());
      setPresetNote(null);
    } catch (e) {
      setLoadError((e as Error).message || 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, [moduleSlug, subjectType, subjectId]);

  useEffect(() => { void load(); }, [load]);

  // Verlassen-Schutz (Regel R5): vorgemerkte Haken gelten als ungespeichert.
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty.size > 0;
  useEffect(() => {
    return registerDirtySource({
      id: `acl-matrix:${moduleSlug}:${subjectType}:${subjectId ?? 'default'}`,
      label: `Rechte ${whom}`,
      isDirty: () => dirtyRef.current,
      discard: () => { setDirty(new Map()); setPresetNote(null); },
    });
  }, [moduleSlug, subjectType, subjectId, whom]);

  const grouped = useMemo(() => {
    const g = new Map<string, CatalogEntry[]>();
    for (const c of catalog) {
      const key = c.category ?? 'Allgemein';
      const arr = g.get(key) ?? [];
      arr.push(c);
      g.set(key, arr);
    }
    return Array.from(g.entries()).map(([category, entries]) => ({ category, entries }));
  }, [catalog]);

  const effective = useCallback((slug: string): Allowed => {
    if (dirty.has(slug)) return dirty.get(slug) ?? null;
    return values.has(slug) ? (values.get(slug) ?? null) : null;
  }, [values, dirty]);

  /** Vormerken; zurück auf den geladenen Wert hebt die Vormerkung auf. */
  const mark = useCallback((slug: string, next: Allowed) => {
    setDirty((prev) => {
      const m = new Map(prev);
      const base = values.has(slug) ? (values.get(slug) ?? null) : null;
      if (next === base) m.delete(slug);
      else m.set(slug, next);
      return m;
    });
  }, [values]);

  const cycle = useCallback((slug: string) => {
    // ─ → ✓ → ✗ → ─
    const cur = effective(slug);
    const next: Allowed = cur === null ? true : cur === true ? false : null;
    mark(slug, next);
  }, [effective, mark]);

  const discard = useCallback(() => {
    setDirty(new Map());
    setPresetNote(null);
  }, []);

  const save = useCallback(async () => {
    if (dirty.size === 0) return;
    setSaving(true);
    try {
      const changes = Array.from(dirty.entries()).map(([permission_slug, allowed]) => ({
        permission_slug,
        allowed,
      }));
      await api.put(`/admin/acl/module-permissions`, {
        module: moduleSlug,
        subject_type: subjectType,
        subject_id: subjectId,
        changes,
        preset: presetNote,
      });
      await load();
      onChanged?.();
    } catch (e) {
      await dlg.error('Rechte wurden nicht gespeichert', e);
    } finally {
      setSaving(false);
    }
  }, [dirty, presetNote, moduleSlug, subjectType, subjectId, load, onChanged, dlg]);

  /**
   * Preset nur vorbelegen (2.02): Rückfrage mit Anzahl und Betroffenem, danach stehen die
   * Haken als Vormerkung in der Matrix — geschrieben wird erst mit „Speichern (n)“.
   */
  const applyPreset = useCallback(async (preset: PresetLevel) => {
    const planned = expandPreset(catalog, preset).filter(([slug, allowed]) => effective(slug) !== allowed);
    if (planned.length === 0) {
      await dlg.notify({
        kind: 'info',
        title: `Preset „${LEVEL_LABEL[preset]}“`,
        message: `Die Haken für ${whom} stehen bereits so, wie das Preset sie setzen würde.`,
      });
      return;
    }
    const ok = await dlg.confirm({
      title: `Preset „${LEVEL_LABEL[preset]}“ vorbelegen?`,
      message: `${planned.length} ${planned.length === 1 ? 'Haken' : 'Haken'} für ${whom} im Modul ${moduleSlug} ${planned.length === 1 ? 'ändert' : 'ändern'} sich. Geschrieben wird erst mit „Speichern (${planned.length})“ — bis dahin lassen sich die Haken noch einzeln anpassen oder verwerfen.`,
      confirmLabel: 'Vorbelegen',
      cancelLabel: 'Abbrechen',
    });
    if (!ok) return;
    setDirty((prev) => {
      const m = new Map(prev);
      for (const [slug, allowed] of planned) {
        const base = values.has(slug) ? (values.get(slug) ?? null) : null;
        if (allowed === base) m.delete(slug);
        else m.set(slug, allowed);
      }
      return m;
    });
    setPresetNote(preset);
  }, [catalog, effective, values, whom, moduleSlug, dlg]);

  if (loading) {
    return <div className="p-3 text-sm text-slate-500">Lade Fein-Rechte …</div>;
  }
  if (loadError) {
    return (
      <div className="p-3 text-sm text-red-700 flex flex-wrap items-center gap-2" data-test-anchor="acl-matrix-error">
        <span>Fein-Rechte konnten nicht geladen werden: {loadError}</span>
        <button type="button" onClick={() => { void load(); }} className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-100">Erneut laden</button>
      </div>
    );
  }
  if (catalog.length === 0) {
    return <div className="p-3 text-sm text-slate-500">Dieses Modul hat keinen Fein-Rechte-Katalog.</div>;
  }

  return (
    <div className="space-y-3 rounded border border-slate-200 bg-slate-50 p-3 text-sm" data-test-anchor="acl-matrix" data-dirty={dirty.size > 0 ? '1' : '0'}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-slate-700">Preset vorbelegen:</span>
        {PRESET_LEVELS.map((lvl) => (
          <button
            key={lvl}
            type="button"
            disabled={saving}
            onClick={() => { void applyPreset(lvl); }}
            data-preset={lvl}
            className={`rounded border px-2 py-1 text-xs font-medium transition ${
              currentLevel === lvl
                ? 'border-brand-600 bg-brand-50 text-brand-700'
                : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
            } disabled:opacity-50`}
            title={`Haken für Level „${LEVEL_LABEL[lvl]}“ vorbelegen — geschrieben wird erst mit Speichern`}
          >
            {LEVEL_LABEL[lvl]}
          </button>
        ))}
        <span className="ml-auto text-xs text-slate-500">
          ─ = Fallback (Level-Preset gilt) · ✓ = erlaubt · ✗ = verboten
        </span>
      </div>

      {grouped.map(({ category, entries }) => (
        <div key={category} className="rounded border border-slate-200 bg-white">
          <div className="border-b border-slate-200 bg-slate-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
            {category}
          </div>
          <ul className="divide-y divide-slate-100">
            {entries.map((e) => {
              const val = effective(e.slug);
              const isDirty = dirty.has(e.slug);
              return (
                <li key={e.slug} className="flex items-center gap-3 px-3 py-1.5" data-permission={e.slug} data-changed={isDirty ? '1' : '0'}>
                  <button
                    type="button"
                    onClick={() => cycle(e.slug)}
                    className={`inline-flex h-7 w-7 items-center justify-center rounded border text-sm font-bold transition ${
                      val === true
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : val === false
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-slate-300 bg-white text-slate-400'
                    } ${isDirty ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}
                    title={val === true ? 'erlaubt (klick: verboten)' : val === false ? 'verboten (klick: Fallback)' : 'Fallback (klick: erlaubt)'}
                  >
                    {val === true ? '✓' : val === false ? '✗' : '─'}
                  </button>
                  <div className="flex flex-1 flex-col">
                    <span className="text-slate-800">{e.label}</span>
                    <span className="font-mono text-[11px] text-slate-500">
                      {e.slug}
                      {e.presetLevel && (
                        <span className="ml-2 text-slate-400">Preset ab: {LEVEL_LABEL[e.presetLevel]}</span>
                      )}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      <div className="flex flex-wrap items-center justify-end gap-2">
        {dirty.size > 0 && (
          <span className="mr-auto text-xs text-amber-700" data-state="dirty">
            {presetNote ? `Vorbelegt aus Preset „${LEVEL_LABEL[presetNote]}“ — ` : ''}ungespeichert
          </span>
        )}
        {dirty.size > 0 && (
          <button
            type="button"
            disabled={saving}
            onClick={discard}
            data-action="discard"
            className="rounded border border-slate-300 bg-white px-3 py-1 text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            Verwerfen ({dirty.size})
          </button>
        )}
        <button
          type="button"
          disabled={saving || dirty.size === 0}
          onClick={() => { void save(); }}
          data-action="save"
          className="rounded bg-brand-600 px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-brand-500 disabled:opacity-50"
        >
          {saving ? 'Speichere …' : dirty.size === 0 ? 'Keine Änderungen' : `Speichern (${dirty.size})`}
        </button>
      </div>
    </div>
  );
}
